import { useMemo, useState, type ComponentType } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, CircularProgress, IconButton, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getSiteById } from '../../../../../api/endpoints/cmsSite';
import type { Site, SiteListResponse } from '../../../../../api/types/cmsSite';
import { ApiErrorAlert } from '../../../../../components/ApiErrorAlert';
import HeaderListPage from '../../header/pages/HeaderListPage';
import SiteTitleListPage from '../../siteTitle/pages/SiteTitleListPage';
import SliderListPage from '../../slider/pages/SliderListPage';
import ContactListPage from '../../contact/pages/ContactListPage';
import AboutListPage from '../../about/pages/AboutListPage';
import ContractListPage from '../../contract/pages/ContractListPage';
import CityListPage from '../../../content/city/pages/CityListPage';
import PopulerCityListPage from '../../../content/populerCity/pages/PopulerCityListPage';
import CheapestDestinationListPage from '../../../content/cheapestDestination/pages/CheapestDestinationListPage';
import DestinationListPage from '../../../content/destination/pages/DestinationListPage';
import CampaignListPage from '../../../content/campaign/pages/CampaignListPage';

const TABS: { label: string; Component: ComponentType<{ siteId: number }> }[] = [
  { label: 'Header', Component: HeaderListPage },
  { label: 'Site Başlıkları', Component: SiteTitleListPage },
  { label: 'Slider', Component: SliderListPage },
  { label: 'İletişim', Component: ContactListPage },
  { label: 'Hakkımızda', Component: AboutListPage },
  { label: 'Sözleşmeler', Component: ContractListPage },
  { label: 'Şehirler', Component: CityListPage },
  { label: 'Popüler Şehirler', Component: PopulerCityListPage },
  { label: 'En Ucuz Rotalar', Component: CheapestDestinationListPage },
  { label: 'Destinasyonlar', Component: DestinationListPage },
  { label: 'Kampanyalar', Component: CampaignListPage },
];

/** Site "Yönet" detail screen — the single landing point for everything besides the Site's
 *  own scalar fields (those stay editable via `SiteFormDialog` on `SiteListPage`). Every
 *  child resource that used to be its own top-level menu item/route
 *  (Header/SiteTitle/Slider/Contact/About/Contract/City/PopulerCity/CheapestDestination/
 *  Destination/Campaign) is now rendered here as a tab, scoped to this one `siteId`. Reuses
 *  `Cms/Site/GetSiteById` — per `docs/backend-F10-cms-response-size.md` + `Docs/api/06`,
 *  post-F-10 this is the light, well-behaved endpoint (~42 KB), unlike the multi-hundred-MB
 *  `Get*` list endpoints. Falls back to the `['cms','sites']` list cache (populated by
 *  `SiteListPage`) if the by-id call fails. */
export default function SiteDetailPage() {
  const { siteId: siteIdParam } = useParams<{ siteId: string }>();
  const siteId = Number(siteIdParam);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);

  const query = useQuery({
    queryKey: ['cms', 'site', siteId],
    queryFn: () => getSiteById(siteId),
    enabled: Number.isFinite(siteId),
  });
  const site = useMemo(() => {
    const fromById = query.data?.sites?.find((s: Site) => s.id === siteId);
    if (fromById) return fromById;
    const listCache = queryClient.getQueryData<SiteListResponse>(['cms', 'sites']);
    return listCache?.sites?.find((s: Site) => s.id === siteId);
  }, [query.data, siteId, queryClient]);

  const ActiveTab = TABS[tab];

  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/cms/site')} aria-label="Site listesine dön">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">{site?.company ?? `Site #${siteId}`}</Typography>
        {query.isFetching && <CircularProgress size={20} />}
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_e, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1 }}
        >
          {TABS.map((t) => (
            <Tab key={t.label} label={t.label} />
          ))}
        </Tabs>
      </Paper>

      <ActiveTab.Component siteId={siteId} />
    </Box>
  );
}
